import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import BeheerLayout from "@/components/BeheerLayout";
import ErrorBoundary from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import Customers from "@/pages/Customers";
import Locations from "@/pages/beheer/Locations";
import HeightProfiles from "@/pages/HeightProfiles";
import Orders from "@/pages/Orders";
import NewOrder from "@/pages/NewOrder";
import OrderDetail from "@/pages/OrderDetail";
import OrderSummary from "@/pages/OrderSummary";
import CalculationResult from "@/pages/CalculationResult";
import CombinedPickList from "@/pages/CombinedPickList";
import Sales from "@/pages/Sales";
import PrintCenter from "@/pages/PrintCenter";
import PrintLabelsCombined from "@/pages/PrintLabelsCombined";
import PrintSummariesCombined from "@/pages/PrintSummariesCombined";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import TenantDetail from "@/pages/TenantDetail";
import ScanLearning from "@/pages/ScanLearning";
import NotFound from "@/pages/NotFound";

export default function App() {
    return (
        <div className="App">
            <ErrorBoundary>
                <BrowserRouter>
                    <Toaster
                        position="top-right"
                        richColors
                        closeButton
                        toastOptions={{
                            classNames: {
                                toast: "font-sans",
                            },
                            "data-testid": "global-toast",
                        }}
                    />
                    <AuthProvider>
                        <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route
                            element={
                                <ProtectedRoute>
                                    <AppShell />
                                </ProtectedRoute>
                            }
                        >
                            <Route path="/" element={<Dashboard />} />
                            <Route path="/dashboard" element={<Navigate to="/" replace />} />
                            <Route path="/bestellingen" element={<Orders />} />
                            <Route path="/bestellingen/nieuw" element={<NewOrder />} />
                            <Route path="/bestellingen/:id/bewerken" element={<NewOrder />} />
                            <Route path="/bestellingen/:id" element={<OrderDetail />} />
                            <Route
                                path="/bestellingen/:id/samenvatting"
                                element={<OrderSummary />}
                            />
                            <Route path="/berekening/:id" element={<CalculationResult />} />
                            <Route path="/printcentrum" element={<PrintCenter />} />
                            <Route path="/printcentrum/labels" element={<PrintLabelsCombined />} />
                            <Route path="/printcentrum/samenvattingen" element={<PrintSummariesCombined />} />
                            <Route path="/raaplijst" element={<Navigate to="/printcentrum" replace />} />
                            <Route path="/raaplijst/gezamenlijk" element={<CombinedPickList />} />
                            <Route path="/verkoop" element={<Sales />} />
                            <Route path="/profiel" element={<Profile />} />

                            {/* Beheer hub */}
                            <Route path="/beheer" element={<BeheerLayout />}>
                                <Route index element={<Navigate to="klanten" replace />} />
                                <Route path="klanten" element={<Customers />} />
                                <Route path="locaties" element={<Locations />} />
                                <Route path="hoogteprofielen" element={<HeightProfiles />} />
                                <Route path="producten" element={<Products />} />
                                <Route
                                    path="scan-learning"
                                    element={
                                        <ProtectedRoute requireRole={["super_admin", "tenant_admin"]}>
                                            <ScanLearning />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="gebruikers"
                                    element={
                                        <ProtectedRoute requireRole={["super_admin", "tenant_admin"]}>
                                            <Settings />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="tenants"
                                    element={
                                        <ProtectedRoute requireRole={["super_admin"]}>
                                            <Settings />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="tenants/:id"
                                    element={
                                        <ProtectedRoute requireRole={["super_admin"]}>
                                            <TenantDetail />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>

                            {/* Backwards-compatible redirects */}
                            <Route path="/producten" element={<Navigate to="/beheer/producten" replace />} />
                            <Route path="/klanten" element={<Navigate to="/beheer/klanten" replace />} />
                            <Route path="/instellingen" element={<Navigate to="/beheer/gebruikers" replace />} />
                            <Route
                                path="/instellingen/tenants/:id"
                                element={<RedirectTenantDetail />}
                            />
                        </Route>
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </AuthProvider>
            </BrowserRouter>
            </ErrorBoundary>
        </div>
    );
}

function RedirectTenantDetail() {
    // Hash-based redirect; React Router 6 has no <Navigate to> with :param,
    // so we read the current path and rewrite.
    const path = window.location.pathname;
    const id = path.split("/").pop();
    return <Navigate to={`/beheer/tenants/${id}`} replace />;
}
